import { strapiService } from '@/services/strapiClient'
import { formatRelativeTime } from '@/utils/formatters'
import type {
  ChatMessage,
  ChatMessageResponse,
  SingleChatMessageResponse,
  ChatMessageFilters,
  CreateMessageData,
} from '@/types/chat'
import type { ToastConfig } from '@/types/form-types'

/**
 * Chat Service - Production-ready chat message operations
 * 
 * Usage with toast notifications and translations:
 * ```typescript
 * import { useToast } from 'primevue/usetoast'
 * import { useI18n } from 'vue-i18n'
 * import { chatService } from '@/services/chatService'
 * 
 * const toast = useToast()
 * const { t } = useI18n()
 * 
 * chatService.setToastService(toast)
 * chatService.setTranslationFunction(t)
 * ```
 */
class ChatService {
  private readonly MAX_FILE_SIZE = 10 * 1024 * 1024
  private readonly MAX_PAGE_SIZE = 100
  private readonly ALLOWED_FILE_TYPES = ['image/', 'video/', 'audio/', 'application/pdf', 'text/']
  
  private toastService?: { add: (toast: ToastConfig) => void }
  private t?: (key: string, values?: Record<string, unknown>) => string

  setToastService(toastService: { add: (toast: ToastConfig) => void }) {
    this.toastService = toastService
  }

  setTranslationFunction(t: (key: string, values?: Record<string, unknown>) => string) {
    this.t = t
  }

  private showUserError(toastKey: string, detailKey?: string, values?: Record<string, unknown>) {
    if (this.toastService && this.t) {
      this.toastService.add({
        severity: 'error',
        summary: this.t(`chat.errors.${toastKey}`),
        detail: detailKey ? this.t(`chat.errors.${detailKey}`, values) : undefined,
        life: 5000,
        closable: true
      })
    }
  }

  private showUserWarning(toastKey: string, detailKey?: string, values?: Record<string, unknown>) {
    if (this.toastService && this.t) {
      this.toastService.add({
        severity: 'warn',
        summary: this.t(`chat.errors.${toastKey}`),
        detail: detailKey ? this.t(`chat.errors.${detailKey}`, values) : undefined,
        life: 4000,
        closable: true
      })
    }
  }
  
  private validateUserId(userId: number, paramName = 'userId'): void {
    if (!Number.isInteger(userId) || userId <= 0) {
      throw new Error(`Invalid ${paramName}: must be a positive integer, got ${userId}`)
    }
  }

  private validatePagination(page: number, pageSize: number): void {
    if (!Number.isInteger(page) || page < 1) {
      throw new Error(`Invalid page: must be a positive integer, got ${page}`)
    }
    if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > this.MAX_PAGE_SIZE) {
      throw new Error(`Invalid pageSize: must be between 1 and ${this.MAX_PAGE_SIZE}, got ${pageSize}`)
    }
  }

  private validateFile(file: File): void {
    if (file.size > this.MAX_FILE_SIZE) {
      const maxSizeMB = Math.round(this.MAX_FILE_SIZE / (1024 * 1024))
      this.showUserError('fileTooLarge', 'fileTooLarge_description', { maxSize: `${maxSizeMB}MB` })
      throw new Error(`File too large: ${file.size} bytes exceeds maximum of ${this.MAX_FILE_SIZE} bytes`)
    }

    if (!this.ALLOWED_FILE_TYPES.some((type) => file.type.startsWith(type))) {
      this.showUserError('invalidFileType', 'invalidFileType_description', { allowedTypes: 'images, videos, audio, PDFs, and text files' })
      throw new Error(`Unsupported file type: ${file.type}`)
    }
  }


  async getMessages(
    filters: ChatMessageFilters = {},
    page = 1,
    pageSize = 20,
  ): Promise<ChatMessageResponse> {
    try {
      this.validatePagination(page, pageSize)

      if (filters.sender) this.validateUserId(filters.sender, 'filters.sender')
      if (filters.receiver) this.validateUserId(filters.receiver, 'filters.receiver')
      if (filters.conversation) this.validateUserId(filters.conversation, 'filters.conversation')

      const params = new URLSearchParams({
        'pagination[page]': page.toString(),
        'pagination[pageSize]': pageSize.toString(),
        sort: 'createdAt:desc',
      })

      if (filters.sender) {
        params.append('filters[sender][id][$eq]', filters.sender.toString())
      }
      if (filters.receiver) {
        params.append('filters[receiver][id][$eq]', filters.receiver.toString())
      }
      if (filters.conversation) {
        params.append('filters[conversation][id][$eq]', filters.conversation.toString())
      }
      if (filters.is_read !== undefined) {
        params.append('filters[is_read][$eq]', filters.is_read.toString())
      }

      const response = await strapiService.find('chat-messages', Object.fromEntries(params))

      const hasMore = response.meta.pagination.page < response.meta.pagination.pageCount
      return {
        ...response,
        meta: {
          ...response.meta,
          hasMore,
        },
      }
    } catch (error) {
      console.error('Failed to get messages:', error)
      throw new Error(`Failed to get messages: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async getConversationMessages(
    conversationId: number,
    page = 1,
    pageSize = 20,
  ): Promise<ChatMessageResponse> {
    this.validateUserId(conversationId, 'conversationId')
    this.validatePagination(page, pageSize)

    return await this.getMessages(
      {
        conversation: conversationId,
      },
      page,
      pageSize,
    )
  }

  async getConversationMessagesByDocumentId(
    conversationDocumentId: string,
    page = 1,
    pageSize = 20,
  ): Promise<ChatMessageResponse> {
    try {
      const conversationResponse = await strapiService.findOne(
        'conversations',
        conversationDocumentId,
      )

      if (!conversationResponse?.data?.id) {
        throw new Error(`Conversation not found for documentId: ${conversationDocumentId}`)
      }

      const conversationId = conversationResponse.data.id

      const params = new URLSearchParams({
        'pagination[page]': page.toString(),
        'pagination[pageSize]': pageSize.toString(),
        sort: 'createdAt:desc',
        'filters[conversations][id][$eq]': conversationId.toString(),
      })

      const response = await strapiService.find('chat-messages', Object.fromEntries(params))

      const hasMore = response.meta?.pagination?.page < response.meta?.pagination?.pageCount
      return {
        ...response,
        meta: {
          ...response.meta,
          hasMore,
        },
      }
    } catch (error) {
      console.error('Failed to get conversation messages:', error)
      throw new Error(
        `Failed to load messages for conversation ${conversationDocumentId}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }


  async createMessage(data: CreateMessageData): Promise<SingleChatMessageResponse> {
    if (!data.text?.trim() && !data.attachment) {
      this.showUserWarning('emptyMessage', 'emptyMessage_description')
      throw new Error('Message must have either text content or attachment')
    }

    if (!data.conversation && !data.receiver) {
      this.showUserError('invalidMessage', 'invalidMessage_description')
      throw new Error('Message must specify either conversation or receiver')
    }

    if (data.receiver) this.validateUserId(data.receiver, 'data.receiver')
    if (data.conversation) this.validateUserId(data.conversation, 'data.conversation')

    const messageData: Record<string, unknown> = {
      is_read: false,
    }

    if (data.text?.trim()) {
      messageData.text = data.text.trim()
    }

    if (data.attachment) {
      messageData.attachment = data.attachment
    }

    if (data.conversation) {
      messageData.conversations = data.conversation
    }

    if (data.receiver) {
      messageData.receiver = data.receiver
    }

    try {
      return await strapiService.create('chat-messages', messageData)
    } catch (error) {
      console.error('Failed to create message:', error)
      this.showUserError('messageFailed', 'messageFailed_description')
      throw new Error(
        `Failed to create message: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }








  async uploadAttachment(
    file: File,
  ): Promise<{ id: number; url: string; name: string; size: number }> {
    this.validateFile(file)

    try {
      const formData = new FormData()
      formData.append('files', file)

      const uploadResponse = await strapiService.files.upload(formData)
      if (!uploadResponse[0]) {
        throw new Error('No file response from upload')
      }
      return uploadResponse[0]
    } catch (error) {
      console.error('Failed to upload attachment:', error)
      this.showUserError('uploadFailed', 'uploadFailed_description')
      throw new Error(
        `Failed to upload attachment: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }

  async markConversationMessagesAsRead(conversationDocumentId: string): Promise<void> {
    try {
      // First, get the conversation by documentId to get its internal ID
      const conversationResponse = await strapiService.findOne(
        'conversations',
        conversationDocumentId,
      )

      if (!conversationResponse?.data?.id) {
        throw new Error(`Conversation not found for documentId: ${conversationDocumentId}`)
      }

      const conversationId = conversationResponse.data.id

      // Find all unread messages for this conversation
      const unreadMessagesParams = new URLSearchParams({
        'filters[conversations][id][$eq]': conversationId.toString(),
        'filters[is_read][$eq]': 'false',
        'pagination[pageSize]': '100', // Handle up to 100 unread messages at once
      })

      const unreadMessagesResponse = await strapiService.find(
        'chat-messages', 
        Object.fromEntries(unreadMessagesParams)
      )

      // Mark all unread messages as read
      const updatePromises = unreadMessagesResponse.data.map((message: { id: number; documentId: string }) =>
        strapiService.update('chat-messages', message.documentId, { is_read: true })
      )

      await Promise.all(updatePromises)

      console.log(`Marked ${unreadMessagesResponse.data.length} messages as read for conversation ${conversationDocumentId}`)
    } catch (error) {
      console.error('Failed to mark conversation messages as read:', error)
      throw new Error(
        `Failed to mark messages as read for conversation ${conversationDocumentId}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }

  async getConversationUnreadCount(conversationDocumentId: string): Promise<number> {
    try {
      // Get the conversation by documentId to get its internal ID
      const conversationResponse = await strapiService.findOne(
        'conversations',
        conversationDocumentId,
      )

      if (!conversationResponse?.data?.id) {
        throw new Error(`Conversation not found for documentId: ${conversationDocumentId}`)
      }

      const conversationId = conversationResponse.data.id

      // Count unread messages for this conversation
      const unreadCountParams = new URLSearchParams({
        'filters[conversations][id][$eq]': conversationId.toString(),
        'filters[is_read][$eq]': 'false',
        'pagination[pageSize]': '1', // We only need the count
      })

      const response = await strapiService.find(
        'chat-messages',
        Object.fromEntries(unreadCountParams)
      )

      return response.meta?.pagination?.total || 0
    } catch (error) {
      console.error('Failed to get conversation unread count:', error)
      return 0
    }
  }

  async getAllUnreadCounts(userOrgId: number): Promise<Record<string, number>> {
    try {
      // Get all conversations for the user using URL params approach
      const conversationParams = new URLSearchParams({
        'pagination[pageSize]': '100', // Get up to 100 conversations
        'fields[]': 'documentId', // Only need documentId
      })
      
      // Add OR filters for participant
      conversationParams.append('filters[$or][0][participant1][id][$eq]', userOrgId.toString())
      conversationParams.append('filters[$or][1][participant_2][id][$eq]', userOrgId.toString())

      const conversationResponse = await strapiService.find('conversations', Object.fromEntries(conversationParams))
      const unreadCounts: Record<string, number> = {}

      // For each conversation, get unread message count
      const countPromises = conversationResponse.data.map(async (conversation: { id: number; documentId: string }) => {
        try {
          // Count unread messages for this conversation using URL params
          const messageParams = new URLSearchParams({
            'pagination[pageSize]': '1', // We only need the count
          })
          
          messageParams.append('filters[conversations][id][$eq]', conversation.id.toString())
          messageParams.append('filters[is_read][$eq]', 'false')

          const unreadCountResponse = await strapiService.find('chat-messages', Object.fromEntries(messageParams))

          const count = unreadCountResponse.meta?.pagination?.total || 0
          if (count > 0) {
            unreadCounts[conversation.documentId] = count
          }
        } catch (error) {
          console.error(`Failed to get unread count for conversation ${conversation.documentId}:`, error)
        }
      })

      await Promise.all(countPromises)
      return unreadCounts
    } catch (error) {
      console.error('Failed to get all unread counts:', error)
      return {}
    }
  }

  formatMessageTime(message: ChatMessage): string {
    return formatRelativeTime(message.createdAt)
  }


}

export const chatService = new ChatService()
export default chatService